<template>
  <button
    type="button"
    class="w-full p-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 hover:border-white/20 transition-all duration-200 flex items-center gap-3 group"
    :class="{
      'border-blue-500/30 bg-blue-500/5': isSelected
    }"
    @click="$emit('select')"
  >
    <!-- Currency Icon -->
    <div
      class="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200"
      :style="{ backgroundColor: currencyColor }"
    >
      <img
        v-if="currencyIcon"
        :src="currencyIcon"
        :alt="option.currency.ticker"
        class="w-6 h-6"
        @error="$event.target.style.display = 'none'"
      />
      <span v-else class="text-xl">🪙</span>
    </div>

    <!-- Currency Info -->
    <div class="flex-1 flex flex-col items-start gap-0.5 min-w-0">
      <div class="flex items-center gap-2 w-full">
        <span class="text-sm font-bold text-vault-text-primary">
          {{ option.currency.ticker.toUpperCase() }}
        </span>
        <span v-if="option.type === 'direct'" class="px-2 py-0.5 bg-green-500/20 text-green-500 text-[10px] font-semibold rounded-full flex-shrink-0">
          Direct
        </span>
        <span v-else class="px-2 py-0.5 bg-orange-500/20 text-orange-500 text-[10px] font-semibold rounded-full flex-shrink-0">
          Exchange
        </span>
      </div>
      <span class="text-xs text-vault-text-secondary/80 truncate w-full">
        {{ option.currency.fullName || option.currency.name }}
      </span>
    </div>

    <!-- Network Tag -->
    <span
      class="px-2.5 py-1 text-xs font-semibold rounded-md flex-shrink-0"
      :style="networkTagStyle"
    >
      {{ networkTagLabel }}
    </span>

    <!-- Checkmark or Arrow -->
    <Check v-if="isSelected" :size="20" class="text-green-500 flex-shrink-0" />
    <ArrowRight v-else :size="16" class="text-vault-text-secondary flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
  </button>
</template>

<script setup>
import { computed } from 'vue'
import { ArrowRight, Check } from 'lucide-vue-next'
import { getCurrencyColor, getNetworkTagStyle, getNetworkTagLabel } from '@/config/networkStyles'

const props = defineProps({
  option: {
    type: Object, // DepositOption
    required: true
  },
  isSelected: {
    type: Boolean,
    default: false
  }
})

defineEmits(['select'])

// Get currency icon URL
const currencyIcon = computed(() => {
  const ticker = props.option.currency.ticker.toLowerCase()
  try {
    return new URL(`../../../node_modules/cryptocurrency-icons/svg/color/${ticker}.svg`, import.meta.url).href
  } catch {
    return null
  }
})

// Get currency icon background color
const currencyColor = computed(() => {
  return getCurrencyColor(props.option.currency.ticker)
})

// Get network tag styling
const networkTagStyle = computed(() => {
  const blockchain = props.option.network?.blockchain || props.option.currency.blockchain
  return getNetworkTagStyle(blockchain)
})

// Get network tag label
const networkTagLabel = computed(() => {
  const blockchain = props.option.network?.blockchain || props.option.currency.blockchain
  return getNetworkTagLabel(blockchain)
})
</script>

<style scoped>
/* Animation for loading spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
